import React from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import * as GiIcons from 'react-icons/gi';
import * as FaIcons from 'react-icons/fa';
import { IoMdAlert, IoMdPin } from 'react-icons/io';
import { BiCog, BiLinkExternal, BiHash } from 'react-icons/bi';
import { MdEdit, MdSave } from 'react-icons/md';
import { FaX, FaPowerOff, FaArrowLeft } from 'react-icons/fa6';
import PageHeader from '../../../../../../components/layout/PageHeader';
import Button from '../../../../../../components/common/Button';
import Badge from '../../../../../../components/common/Badge';
import BtnSync from '../../../../../../components/BtnSync';
import NumberWithPercentage from '../../../../../../components/NumberWithPercentage';
import {
  translate,
  verifyHideCountry,
  verifyHideCountryItems,
} from '../../../../../../utils/Common';
import { verifyAccessViewPermissions } from '../../../../../../config/permissions';
import AuthContext from '../../../../../../contexts/auth';
import styles from './PlatformDetailsHeader.module.css';

const PlatformDetailsHeader = ({
  platformSelected,
  platformDetails,
  editActive,
  control,
  errors,
  handleEditToggle,
  onBack,
  actionsPerms,
  onResetLocker,
}) => {
  const location = useLocation();
  const userContext = React.useContext(AuthContext);
  const permissionsPathGeneral = '/dashboard/platforms/general';
  const canEditConfiguration = verifyAccessViewPermissions(['edit-configuration'], {
    path: permissionsPathGeneral,
  });

  const name = platformDetails?.Nombre ?? platformSelected?.locationName ?? platformSelected?.Plataforma ?? '-';
  const isOnline = platformSelected?.locationStatus === 'ONLINE';
  const isInactive = platformSelected?.statusId === 2 || platformSelected?.IdEstatus === 2;

  const available = platformSelected?.availableDoors ?? 0;
  const total = platformSelected?.totalDoors ?? 0;
  const ocupationLocker = platformSelected?.ocupationLocker ?? 0;

  const lastConnectionDate = platformSelected?.lastConnectionDate ?? platformDetails?.FechaCreacion;
  const locationAddress = platformSelected?.locationAddress ?? platformDetails?.Direccion_Fisica ?? '';

  const lastConnFormatted = (() => {
    if (!lastConnectionDate) return null;
    try {
      const d = new Date(lastConnectionDate);
      const lang = localStorage.getItem('language') || 'es';
      const localeMap = { es: 'es-ES', en: 'en-US', pt: 'pt-BR' };
      const locale = localeMap[lang] || 'es-ES';
      const dateStr = d.toLocaleDateString(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
      const timeStr = d.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      return { dateStr, timeStr };
    } catch {
      return null;
    }
  })();

  const idItems = [];
  if (platformSelected?.zipCode) {
    idItems.push({
      key: 'zip',
      label: localStorage.getItem('country') === 'br'
        ? `CEP: ${platformSelected.zipCode}`
        : `Zip: ${platformSelected.zipCode}`,
      icon: null,
    });
  }
  if (platformSelected?.locationId) {
    idItems.push({
      key: 'extId',
      label: `ExtId: ${platformSelected.locationId}`,
      icon: <BiLinkExternal size={12} />,
    });
  }
  if (platformSelected?.boxitLocationId) {
    idItems.push({
      key: 'id',
      label: `ID. ${platformSelected.boxitLocationId}`,
      icon: <BiHash size={12} />,
    });
  }

  // El breadcrumb y el botón "Back" hacen lo mismo: los dos van al listado de
  // plataformas.
  const breadcrumbClick = (e) => {
    e.preventDefault();
    onBack();
  };

  return (
    <PageHeader
      title={
        editActive ? (
          <Controller
            name="nombre"
            control={control}
            rules={{ required: { value: true, message: translate('global.isRequerid') } }}
            render={(field) => (
              <input
                {...field}
                type="text"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                className={`${styles.nameInput} ${errors.nombre ? styles.nameInputError : ''}`}
                id="nombre"
                placeholder={translate('global.name')}
              />
            )}
          />
        ) : (
          name
        )
      }
      breadcrumbs={[
        <span
          key="platforms"
          onClick={breadcrumbClick}
          className={styles.breadcrumbLink}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && breadcrumbClick(e)}
        >
          {translate('global.lockers')}
        </span>,
        name,
      ]}
      subtitle={
        <div className={styles.subtitleContent}>
          {idItems.length > 0 && (
            <span className={styles.idsRow}>
              {idItems.map((item, idx) => (
                <span key={item.key} className={styles.idItem}>
                  {idx > 0 && <span className={styles.idSeparator}>·</span>}
                  {item.icon && <span className={styles.idIcon}>{item.icon}</span>}
                  {item.label}
                </span>
              ))}
            </span>
          )}
          <div className={styles.badgesRow}>
            <Badge
              variant={isOnline ? 'success' : 'error'}
              size="sm"
              icon={isOnline ? <FaIcons.FaSignal size={12} /> : <IoMdAlert size={12} />}
            >
              {isOnline ? 'Online' : 'Offline'}
            </Badge>
            {isInactive && (
              <Badge variant="archived" size="sm">
                {translate('global.inactive')}
              </Badge>
            )}
            {total > 0 && (
              <div className={styles.doorsRatio}>
                <span className={styles.doorsRatioValue} data-variant={available === 0 ? 'empty' : available <= total * 0.2 ? 'low' : 'ok'}>
                  {available}/{total}
                </span>
                <div className={styles.doorsRatioBar} role="presentation">
                  <div
                    className={styles.doorsRatioFill}
                    style={{ width: `${total > 0 ? (available / total) * 100 : 0}%` }}
                    data-variant={available === 0 ? 'empty' : available <= total * 0.2 ? 'low' : 'ok'}
                  />
                </div>
              </div>
            )}
            <NumberWithPercentage number={ocupationLocker} />
            {lastConnFormatted && (userContext.user?.roleId > 4 || userContext.user?.RoleBli > 4) && (
              <div className={styles.lastConnection}>
                <span className={styles.lastConnectionLabel}>
                  {translate('dashboard.platforms.details.lastConnection')}
                </span>
                <span className={styles.lastConnectionDate}>{lastConnFormatted.dateStr}</span>
                <span className={styles.lastConnectionTime}>
                  <FaIcons.FaClock size={10} />
                  {lastConnFormatted.timeStr}
                </span>
              </div>
            )}
          </div>
          {locationAddress && (
            <span className={styles.addressRow}>
              <IoMdPin size={14} className={styles.addressIcon} />
              {locationAddress}
            </span>
          )}
        </div>
      }
      icon={<GiIcons.GiPlatform size={28} />}
      actions={
        <div className={styles.headerActions}>
          <Button type="button" variant="outline" size="sm" onClick={onBack} leftIcon={<FaArrowLeft size={14} />}>
            {translate('global.return')}
          </Button>
          {actionsPerms.includes('update_locker') && (
            <>
              {/* Botón de MODIFICAR temporalmente oculto
              {!editActive ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEditToggle();
                  }}
                  leftIcon={<MdEdit size={16} />}
                >
                  {translate('global.modify')}
                </Button>
              ) : ( */}
              {editActive && (
                <>
                  <Button
                    type="submit"
                    form="platform-details-form"
                    variant="primary"
                    size="sm"
                    leftIcon={<MdSave size={16} />}
                  >
                    {translate('global.save')}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={handleEditToggle} leftIcon={<FaX size={14} />}>
                    {translate('global.close')}
                  </Button>
                </>
              )}
            </>
          )}
          {canEditConfiguration && verifyHideCountryItems(['pa']) && platformSelected?.locationId && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                window.location.href = `/dashboard/platforms/settings?extId=${platformSelected.locationId}&view=edit`;
              }}
              leftIcon={<BiCog size={16} />}
            >
              {translate('global.configuration')}
            </Button>
          )}
          {verifyAccessViewPermissions(['reset-locker'], { path: location.pathname }) && (
            <Button type="button" variant="outline" size="sm" onClick={onResetLocker} leftIcon={<FaPowerOff size={14} />}>
              {translate('global.resetLocker')}
            </Button>
          )}
          {verifyAccessViewPermissions(['sync-all'], { path: permissionsPathGeneral }) && (
            <>
              <BtnSync
                id={`btn-sync-detail-${platformSelected?.locationId}`}
                mode="force"
                locationId={platformSelected?.locationId}
                size="sm"
                variant="outline"
              />
              {/* Sync parcial: solo los clientes del locker. Usa boxitLocationId (IdPlataforma),
                  no el locationId externo que consume el sync completo. */}
              {platformSelected?.boxitLocationId && (
                <BtnSync
                  id={`btn-sync-clients-${platformSelected.boxitLocationId}`}
                  mode="locker-clients"
                  platformId={platformSelected.boxitLocationId}
                  label={translate('dashboard.platforms.details.syncClients')}
                  size="sm"
                  variant="outline"
                />
              )}
            </>
            )}
        </div>
      }
      divider={false}
    />
  );
};

PlatformDetailsHeader.propTypes = {
  platformSelected: PropTypes.object.isRequired,
  platformDetails: PropTypes.object,
  editActive: PropTypes.bool.isRequired,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  handleEditToggle: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  actionsPerms: PropTypes.array.isRequired,
  onResetLocker: PropTypes.func.isRequired,
};

export default PlatformDetailsHeader;
